import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { InfoCircle } from 'react-iconly';
import { getOrderById, updateOrderStatus, updateOrder, subscribeToOrder } from '../utils/orderApi';
import { BackButton } from '../components/ui';
import { OrderChat } from '../components/OrderChat';
import { CustomerOrderChat } from '../components/CustomerOrderChat';
import { ReviewForm } from '../components/ui/ReviewForm';
import { StarRating } from '../components/ui/StarRating';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { formatOrderDate, formatOrderTime } from '../lib/dateUtils';
import { orderToasts } from '../lib/utils/toast';
import { useAuth } from '../contexts/AuthContext';
import { getStoreNameBySellerId } from '../lib/messagesService';
import { GRADIENT_UTILS } from '../lib/styleGuide';
import { fetchShopBySeller } from '../lib/hooks/useShops';
import { 
  createMultipleReviews, 
  getOrderReviews, 
  canReviewProduct,
  updateReview,
  deleteReview 
} from '../lib/reviewsService';

// Style guide colors
const PRIMARY_BROWN = '#C97B53';
const NEUTRAL_BG = '#F9F9F9';
const NEUTRAL_WHITE = '#FFF';
const NEUTRAL_TEXT = '#222';
const ACCENT_GRAY = '#757575';
const SUCCESS_GREEN = '#4CAF50';
const WARNING_ORANGE = '#FF9800';
const ERROR_RED = '#F44336';

const headingFont = { fontFamily: 'Sora, sans-serif', fontWeight: 700 };
const bodyFont = { fontFamily: 'Montserrat, sans-serif', fontWeight: 500 };

// Fallback mock order data
const mockOrder = {
  id: 'ORD-2024-001',
  status: 'processing',
  statusTimeline: [
    { key: 'confirmed', label: 'Order Confirmed', timestamp: '2024-06-01T18:30:00Z', complete: true, info: 'We received your order.' },
    { key: 'processing', label: 'Processing', timestamp: '2024-06-01T19:00:00Z', complete: true, info: 'Your order is being prepared.' },
    { key: 'out', label: 'Out for Delivery', timestamp: null, complete: false, info: 'Your order is on the way.' },
    { key: 'delivered', label: 'Delivered', timestamp: null, complete: false, info: 'Order delivered to your address.' },
  ],
  date: '2024-06-01T18:30:00Z',
  estDelivery: '2024-06-01T20:00:00Z',
  paymentMethod: 'Visa •••• 1234',
  items: [
    { id: '1', name: 'Pasta Carbonara', image: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=80', price: 18.99 },
    { id: '2', name: 'Garlic Bread', image: 'https://images.unsplash.com/photo-1519864600265-abb23847ef2c?w=80', price: 5.99 }
  ],
  subtotal: 24.98,
  shipping: 2.99,
  tax: 2.00,
  total: 29.97,
  shippingAddress: {
    name: 'Alex Johnson',
    street: '123 New Apartment Rd',
    city: 'Springfield',
    zip: '12345',
    phone: '(555) 123-4567',
  },
};

const STATUS_COLORS = {
  confirmed: SUCCESS_GREEN,
  processing: WARNING_ORANGE,
  out: PRIMARY_BROWN,
  delivered: ACCENT_GRAY,
};

const STATUS_LABELS = {
  confirmed: 'Order Confirmed',
  processing: 'Processing',
  out: 'Out for Delivery',
  delivered: 'Delivered',
};

// Status workflow logic from Orders.js
const STATUS_WORKFLOW = {
  placed: { tag: 'NEW', key: 'new', action: 'Accept Order', next: 'accepted' },
  accepted: { tag: 'ACCEPTED', key: 'accepted', action: 'Start Preparing', next: 'in-progress' },
  'in-progress-pickup': { tag: 'PREPARING', key: 'preparing', action: 'Mark as Ready', next: 'ready' },
  'in-progress-delivery': { tag: 'PREPARING', key: 'preparing', action: 'Start Delivery', next: 'out-for-delivery' },
  'ready': { tag: 'READY', key: 'ready', action: 'Mark as Complete', next: 'completed' },
  'out-for-delivery': { tag: 'DELIVERING', key: 'delivering', action: 'Mark as Complete', next: 'completed' },
  'completed': { tag: 'COMPLETED', key: 'completed', action: null, next: null },
  'canceled': { tag: 'CANCELED', key: 'canceled', action: null, next: null },
};

function getWorkflowKey(order) {
  switch (order.status) {
    case 'placed':
      return 'placed';
    case 'confirmed':
      return 'placed';
    case 'accepted':
      return 'accepted';
    case 'processing':
      return order.type === 'delivery' ? 'in-progress-delivery' : 'in-progress-pickup';
    case 'in-progress':
      return order.type === 'delivery' ? 'in-progress-delivery' : 'in-progress-pickup';
    case 'ready':
      return 'ready';
    case 'out-for-delivery':
      return 'out-for-delivery';
    case 'delivered':
    case 'completed':
      return 'completed';
    case 'cancelled':
    case 'canceled':
      return 'canceled';
    default:
      return order.status;
  }
}

// Using date-fns utilities for date and time formatting

function formatOrderNumber(orderId) {
  if (!orderId) return 'Order #000000';
  // Handle different ID formats
  const idString = String(orderId);
  const shortId = idString.length >= 6 ? idString.slice(-6).toUpperCase() : idString.toUpperCase();
  return `Order #${shortId}`;
}

const CANCELLATION_REASONS = [
  'Item out of stock',
  'Customer request',
  'Unable to fulfill',
  'Other'
];

function getItemName(items, idx) {
  if (Array.isArray(items) && typeof items[idx] === 'object' && items[idx] !== null && 'name' in items[idx]) {
    return items[idx].name || 'Unnamed Item';
  }
  return 'Unnamed Item';
}

function ConfirmationModal({ open, onClose, onConfirm, title, message, reason, setReason, confirmLabel }) {
  if (!open) return null;
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: '#0008', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 32, maxWidth: 380, width: '100%', boxShadow: '0 4px 24px #0002', display: 'flex', flexDirection: 'column', gap: 18 }}>
        <h2 style={{ ...headingFont, fontSize: 20, color: NEUTRAL_TEXT, margin: 0 }}>{title}</h2>
        <div style={{ color: ACCENT_GRAY, fontSize: 15, ...bodyFont }}>{message}</div>
        <label style={{ fontSize: 14, color: ACCENT_GRAY, fontWeight: 600, marginTop: 8 }}>Reason for cancellation (optional)</label>
        <select value={reason} onChange={e => setReason(e.target.value)} style={{ padding: 8, borderRadius: 8, border: '1px solid #E0E0E0', fontSize: 15, ...bodyFont }}>
          <option value="">Select a reason...</option>
          {CANCELLATION_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
          <button onClick={onClose} style={{ flex: 1, background: NEUTRAL_WHITE, color: PRIMARY_BROWN, border: `2px solid ${PRIMARY_BROWN}`, borderRadius: 10, padding: '12px 0', fontSize: 16, fontWeight: 700, cursor: 'pointer', ...bodyFont }}>Go Back</button>
          <button onClick={onConfirm} style={{ flex: 1, background: ERROR_RED, color: '#fff', border: 'none', borderRadius: 10, padding: '12px 0', fontSize: 16, fontWeight: 700, cursor: 'pointer', ...bodyFont }}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

export default function OrderDetails() {
  console.log('🚀 OrderDetails component rendering');
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [showInfo, setShowInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState(false);
  const [showCancelOrder, setShowCancelOrder] = useState(false);
  const [showCancelItem, setShowCancelItem] = useState(null); // item index or null
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [shopNames, setShopNames] = useState({});
  const [shopAddresses, setShopAddresses] = useState({}); // Store shop addresses by sellerId
  
  // Review state
  const [showReviews, setShowReviews] = useState(false);
  const [existingReviews, setExistingReviews] = useState([]);
  const [reviewForms, setReviewForms] = useState({});
  const [isSubmittingReviews, setIsSubmittingReviews] = useState(false);
  
  // Add user role check
  const { currentUser, userRole, hasRole } = useAuth();

  // Review functions
  const loadExistingReviews = async () => {
    console.log('🔍 loadExistingReviews called - order:', order, 'currentUser:', currentUser?.uid);
    
    // Resolve order ID from different possible locations
    const resolvedOrderId = order?.id || 
                           order?.parentOrder?.id || 
                           (order?.merchantOrders && order.merchantOrders[0]?.parentOrderId) ||
                           orderId; // fallback to URL param from useParams
    
    console.log('🔍 Resolved order ID:', resolvedOrderId);
    
    if (!resolvedOrderId || !currentUser?.uid) {
      console.log('❌ loadExistingReviews early return - missing resolvedOrderId or currentUser.uid:', { resolvedOrderId, currentUserId: currentUser?.uid });
      return;
    }
    
    try {
      console.log('🔍 Loading reviews for order:', resolvedOrderId);
      const reviews = await getOrderReviews(resolvedOrderId);
      console.log('📝 Existing reviews loaded:', reviews);
      console.log('📝 Reviews count:', reviews.length);
      console.log('📝 Reviews details:', reviews.map(r => ({ id: r.id, productId: r.productId, rating: r.rating })));
      setExistingReviews(reviews);
      
      // Initialize review forms for products that can be reviewed
      const forms = {};
      
      // Check merchantOrders structure first (multi-vendor orders)
      if (order.merchantOrders && Array.isArray(order.merchantOrders)) {
        console.log('🏪 Processing merchantOrders:', order.merchantOrders.length);
        for (const merchantOrder of order.merchantOrders) {
          if (merchantOrder.items && Array.isArray(merchantOrder.items)) {
            console.log('📦 Processing items in merchantOrder:', merchantOrder.items.length);
            for (const item of merchantOrder.items) {
              if (item && item.id) {
                console.log('🔍 Checking if can review product:', item.id, item.name);
                console.log('🔍 Full item object:', item);
                const canReview = await canReviewProduct(resolvedOrderId, item.id, currentUser.uid);
                console.log('✅ Can review product', item.id, ':', canReview);
                if (canReview) {
                  const existingReview = reviews.find(r => r.productId === item.id);
                  forms[item.id] = {
                    rating: existingReview?.rating || 0,
                    comment: existingReview?.comment || '',
                    existingReviewId: existingReview?.id || null
                  };
                  console.log('📝 Created review form for', item.id, ':', forms[item.id]);
                }
              }
            }
          }
        }
      } 
      // Fallback to order.items structure (single-vendor orders)
      else if (order.items && Array.isArray(order.items)) {
        console.log('📦 Processing order.items:', order.items.length);
        for (const item of order.items) {
          if (item && item.id) {
            console.log('🔍 Checking if can review product:', item.id, item.name);
            const canReview = await canReviewProduct(resolvedOrderId, item.id, currentUser.uid);
            console.log('✅ Can review product', item.id, ':', canReview);
            if (canReview) {
              const existingReview = reviews.find(r => r.productId === item.id);
              forms[item.id] = {
                rating: existingReview?.rating || 0,
                comment: existingReview?.comment || '',
                existingReviewId: existingReview?.id || null
              };
            }
          }
        }
      }
      
      console.log('📝 Final review forms:', forms);
      setReviewForms(forms);
    } catch (error) {
      console.error('Error loading reviews:', error);
    }
  };

  const handleReviewSubmit = async (productId, reviewData) => {
    // Resolve order ID from different possible locations
    const resolvedOrderId = order?.id || 
                           order?.parentOrder?.id || 
                           (order?.merchantOrders && order.merchantOrders[0]?.parentOrderId) ||
                           orderId; // fallback to URL param from useParams
    
    if (!resolvedOrderId || !currentUser?.uid) return;
    
    try {
      setIsSubmittingReviews(true);
      
      const reviewPayload = {
        orderId: resolvedOrderId,
        orderGroupId: resolvedOrderId, // Using order ID as group ID for simplicity
        productId,
        customerId: currentUser.uid,
        rating: reviewData.rating,
        comment: reviewData.comment
      };
      
      if (reviewForms[productId]?.existingReviewId) {
        // Update existing review
        await updateReview(reviewForms[productId].existingReviewId, {
          rating: reviewData.rating,
          comment: reviewData.comment
        });
      } else {
        // Create new review
        await createMultipleReviews([reviewPayload]);
      }
      
      // Reload reviews
      await loadExistingReviews();
      
      orderToasts.success('Review submitted successfully!');
    } catch (error) {
      console.error('Error submitting review:', error);
      orderToasts.error('Failed to submit review. Please try again.');
    } finally {
      setIsSubmittingReviews(false);
    }
  };

  const handleReviewCancel = (productId) => {
    setReviewForms(prev => ({
      ...prev,
      [productId]: {
        rating: 0,
        comment: '',
        existingReviewId: null
      }
    }));
  };

  // Component logic here

  // Replace order fetching with real-time subscription
  useEffect(() => {
    if (!orderId) return;
    setLoading(true);
    setError('');
    
    // Use the new multi-vendor order system
    const fetchOrderData = async () => {
      try {
        const { getParentOrderWithDetails, subscribeToParentOrder } = await import('../utils/multiVendorOrderApi');
        
        // Initial fetch
        const orderData = await getParentOrderWithDetails(orderId);
        setOrder(orderData);
        setError('');
        setLoading(false);
        
        // Set up real-time subscription
        const unsubscribe = subscribeToParentOrder(orderId, (updatedOrderData) => {
          if (updatedOrderData) {
            setOrder(updatedOrderData);
          setError('');
        } else {
          setError('Order not found.');
          setOrder(null);
        }
        setLoading(false);
        });
        
        return unsubscribe;
      } catch (err) {
        console.error('[OrderDetails] Error in fresh fetch:', err);
        setError('Failed to load order');
        setLoading(false);
      }
    };

    // Do fresh fetch and set up subscription
    fetchOrderData().then(unsubscribe => {
      if (unsubscribe) {
        return () => unsubscribe();
      }
    });
  }, [orderId]);

  // Fetch shop names and addresses when order data changes
  useEffect(() => {
    if (!order) return;
    
    const fetchShopData = async () => {
      const sellerIds = new Set();
      
      // Collect all seller IDs from the order
      if (order.merchantOrders) {
        order.merchantOrders.forEach(merchantOrder => {
          if (merchantOrder.sellerId) {
            sellerIds.add(merchantOrder.sellerId);
          }
        });
      }
      
      // Fetch shop names and addresses for all seller IDs
      const shopNamesMap = {};
      const shopAddressesMap = {};
      
      // Check if any part of the order is ready to determine if we should fetch addresses
      const orderStatus = order.status || (order.merchantOrders && order.merchantOrders[0]?.status) || 'placed';
      const hasReadyStatus = orderStatus?.toLowerCase() === 'ready' || 
                            (order.merchantOrders && order.merchantOrders.some(mo => 
                              (mo.status?.toLowerCase() === 'ready') || 
                              (mo.status?.toUpperCase() === 'READY') ||
                              (mo.items && mo.items.some(item => 
                                item.status?.toLowerCase() === 'ready' || 
                                item.status?.toUpperCase() === 'READY'
                              ))
                            ));
      
      for (const sellerId of sellerIds) {
        try {
          // Fetch shop name
          const shopName = await getStoreNameBySellerId(sellerId);
          shopNamesMap[sellerId] = shopName;
          
          // Fetch shop address if order has any "ready" status
          // We'll fetch addresses for all sellers when any is ready, but only display for ready groups
          if (hasReadyStatus) {
            try {
              const shopData = await fetchShopBySeller(sellerId);
              // Extract address from various possible field names
              const address = shopData.address || {};
              const shopAddress = {
                street: address.street || shopData.street || shopData.storeAddress || '',
                city: address.city || shopData.city || shopData.storeCity || '',
                state: address.state || shopData.state || shopData.storeState || shopData.shopState || '',
                zip: address.zip || shopData.zip || shopData.postalCode || shopData.storeZip || '',
              };
              
              // Only store if we have at least street and city
              if (shopAddress.street || shopAddress.city) {
                shopAddressesMap[sellerId] = shopAddress;
              }
            } catch (error) {
              console.error('Error fetching shop address for seller:', sellerId, error);
            }
          }
        } catch (error) {
          console.error('Error fetching shop name for seller:', sellerId, error);
          shopNamesMap[sellerId] = `Seller ${sellerId.slice(-4)}`;
        }
      }
      
      setShopNames(shopNamesMap);
      setShopAddresses(shopAddressesMap);
    };
    
    fetchShopData();
  }, [order]);

  // Load reviews when order is completed
  console.log('🔧 About to define review useEffect');
  useEffect(() => {
    console.log('🔄 Review useEffect triggered - order:', !!order, 'currentUser:', !!currentUser?.uid);
    if (order && currentUser?.uid) {
      // Check if order is completed by looking at the status
      const orderStatus = order.status || (order.merchantOrders && order.merchantOrders[0]?.status) || 'placed';
      console.log('🔄 Review useEffect - orderStatus:', orderStatus, 'will load reviews:', orderStatus === 'completed');
      if (orderStatus === 'completed') {
        console.log('🔄 Calling loadExistingReviews...');
        loadExistingReviews();
      }
    } else {
      console.log('🔄 Review useEffect - missing order or currentUser:', { hasOrder: !!order, hasCurrentUser: !!currentUser?.uid });
    }
  }, [order, currentUser?.uid]);

  // Also run when showReviews changes to true (when user clicks "Write Reviews")
  useEffect(() => {
    console.log('🔄 ShowReviews useEffect triggered - showReviews:', showReviews, 'order:', !!order, 'currentUser:', !!currentUser?.uid);
    if (showReviews && order && currentUser?.uid) {
      const orderStatus = order.status || (order.merchantOrders && order.merchantOrders[0]?.status) || 'placed';
      console.log('🔄 ShowReviews useEffect - orderStatus:', orderStatus, 'will load reviews:', orderStatus === 'completed');
      if (orderStatus === 'completed') {
        console.log('🔄 Calling loadExistingReviews from showReviews...');
        loadExistingReviews();
      }
    }
  }, [showReviews, order, currentUser?.uid]);

  // Add cancel order logic
  async function handleCancelOrder() {
    setCancelling(true);
    try {
      await updateOrderStatus(order.id, 'cancelled');
      setShowCancelOrder(false);
      setCancelReason('');
    } catch (err) {
      orderToasts.error('cancel');
    } finally {
      setCancelling(false);
    }
  }

  // Add cancel item logic
  async function handleCancelItem(idx) {
    setCancelling(true);
    try {
      const item = Array.isArray(order.items) && typeof order.items[idx] === 'object' && order.items[idx] !== null ? order.items[idx] : null;
      if (!item) throw new Error('Invalid item');
      const newItems = order.items.filter((_, i) => i !== idx);
      const newSubtotal = newItems.reduce((sum, it) => sum + (it.price * (it.qty || it.quantity || 1)), 0);
      const newTaxes = Math.round(newSubtotal * 0.08 * 100) / 100;
      const newTotal = newSubtotal + newTaxes;
      await updateOrder(order.id, { items: newItems, total: newTotal });
      setShowCancelItem(null);
      setCancelReason('');
    } catch (err) {
      orderToasts.error('cancel');
    } finally {
      setCancelling(false);
    }
  }

  // Add status color helper
  function getStatusColor(status) {
    switch (status) {
      case 'pending': return WARNING_ORANGE;
      case 'preparing': return PRIMARY_BROWN;
      case 'ready': return SUCCESS_GREEN;
      case 'delivered': return ACCENT_GRAY;
      case 'completed': return ACCENT_GRAY;
      default: return ACCENT_GRAY;
    }
  }

  // Add financial summary logic - handle different order structures
  let subtotal = 0;
  let shipping = 0;
  let tax = 0;
  let total = 0;

  // Only calculate if order exists
  if (order) {
    // Try to get values from different possible locations in the order structure
    if (order.parentOrder?.totals && order.parentOrder.totals.subtotal > 0) {
      // Parent order has meaningful totals (not zeros)
      subtotal = (order.parentOrder.totals.subtotal || 0) / 100;
      shipping = (order.parentOrder.totals.shipping || 0) / 100;
      tax = (order.parentOrder.totals.tax || 0) / 100;
      total = (order.parentOrder.totals.total || 0) / 100;
    } else if (order.merchantOrders && order.merchantOrders.length > 0) {
      // Calculate from merchant orders (values are already in dollars, not cents)
      subtotal = order.merchantOrders.reduce((sum, merchantOrder) => {
        return sum + (merchantOrder.totals?.subtotal || 0);
      }, 0);
      shipping = order.merchantOrders.reduce((sum, merchantOrder) => {
        return sum + (merchantOrder.totals?.shipping || 0);
      }, 0);
      tax = order.merchantOrders.reduce((sum, merchantOrder) => {
        return sum + (merchantOrder.totals?.tax || 0);
      }, 0);
      total = order.merchantOrders.reduce((sum, merchantOrder) => {
        return sum + (merchantOrder.totals?.total || 0);
      }, 0);
    } else if (order.items && order.items.length > 0) {
      // Calculate from items
      subtotal = order.items.reduce((sum, item) => sum + (Number(item.price || 0) * (item.qty || item.quantity || 1)), 0);
      tax = Math.round(subtotal * 0.08 * 100) / 100;
      total = subtotal + tax;
    } else if (order.merchantOrders && order.merchantOrders.length > 0 && order.merchantOrders[0].items) {
      // Try to calculate from merchant order items
      const merchantOrder = order.merchantOrders[0];
      subtotal = merchantOrder.items.reduce((sum, item) => {
        const price = Number(item.price || item.unitPrice || 0);
        const quantity = item.qty || item.quantity || 1;
        return sum + (price * quantity);
      }, 0);
      tax = Math.round(subtotal * 0.08 * 100) / 100;
      total = subtotal + tax;
    } else {
      // Fallback to direct order properties
      subtotal = (order.subtotal || 0) / 100;
      shipping = (order.shipping || 0) / 100;
      tax = (order.tax || 0) / 100;
      total = (order.total || 0) / 100;
    }

    // Debug financial data
    console.log('[OrderDetails] Financial data debug:');
    console.log('[OrderDetails] Full order object:', order);
    console.log('[OrderDetails] order.parentOrder:', order.parentOrder);
    console.log('[OrderDetails] order.parentOrder?.totals:', order.parentOrder?.totals);
    console.log('[OrderDetails] order.merchantOrders:', order.merchantOrders);
    console.log('[OrderDetails] order.merchantOrders[0]:', order.merchantOrders?.[0]);
    console.log('[OrderDetails] order.merchantOrders[0]?.totals:', order.merchantOrders?.[0]?.totals);
    console.log('[OrderDetails] order.merchantOrders[0]?.items:', order.merchantOrders?.[0]?.items);
    console.log('[OrderDetails] order.items:', order.items);
    console.log('[OrderDetails] order.subtotal:', order.subtotal);
    console.log('[OrderDetails] order.total:', order.total);
    console.log('[OrderDetails] Calculated values - subtotal:', subtotal, 'shipping:', shipping, 'tax:', tax, 'total:', total);
  }

  const PLACEHOLDER_IMAGE = 'https://via.placeholder.com/48?text=Food';

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: NEUTRAL_BG, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: ACCENT_GRAY }}>
          <div style={{ fontSize: 18, marginBottom: 8 }}>Loading order details...</div>
          <div style={{ fontSize: 14 }}>Please wait</div>
        </div>
      </div>
    );
  }

  if (error && !order) {
    return (
      <div style={{ minHeight: '100vh', background: NEUTRAL_BG, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: ERROR_RED }}>
          <div style={{ fontSize: 18, marginBottom: 8 }}>Error loading order</div>
          <div style={{ fontSize: 14, marginBottom: 16 }}>{error}</div>
          <button 
            onClick={() => navigate(-1)}
            style={{ 
              background: PRIMARY_BROWN, 
              color: NEUTRAL_WHITE, 
              border: 'none', 
              borderRadius: 8, 
              padding: '12px 24px', 
              fontSize: 16, 
              fontWeight: 600, 
              cursor: 'pointer',
              ...bodyFont 
            }}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div style={{ minHeight: '100vh', background: NEUTRAL_BG, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: ACCENT_GRAY }}>
          <div style={{ fontSize: 18, marginBottom: 8 }}>Order not found</div>
          <div style={{ fontSize: 14, marginBottom: 16 }}>The order you're looking for doesn't exist.</div>
          <button 
            onClick={() => navigate(-1)}
            style={{ 
              background: PRIMARY_BROWN, 
              color: NEUTRAL_WHITE, 
              border: 'none', 
              borderRadius: 8, 
              padding: '12px 24px', 
              fontSize: 16, 
              fontWeight: 600, 
              cursor: 'pointer',
              ...bodyFont 
            }}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Action button logic
  // Handle different order structures - check if it's a parent order with merchant orders
  const orderStatus = order ? (order.status || (order.merchantOrders && order.merchantOrders[0]?.status) || 'placed') : 'placed';
  const workflowKey = order ? getWorkflowKey({ ...order, status: orderStatus }) : 'placed';
  const workflow = STATUS_WORKFLOW[workflowKey] || {};
  const canCancel = order ? !['completed', 'canceled'].includes(orderStatus) : false;
  
  // Check if user is seller (has seller role or is the seller of this order)
  // Handle different possible field names for seller and user IDs
  const sellerId = order ? (order.sellerId || order.seller_id || order.creatorId || order.creator_id || 
                   (order.merchantOrders && order.merchantOrders[0]?.sellerId) ||
                   (order.merchantOrders && order.merchantOrders[0]?.creatorId)) : null;
  const userId = order ? (order.userId || order.user_id || order.customerId || order.customer_id ||
                 (order.parentOrder && order.parentOrder.buyerId) ||
                 (order.parentOrder && order.parentOrder.userId)) : null;
  
  // More specific role checking - only consider as seller if they have seller role OR are the actual seller of this order
  const hasSellerRole = hasRole('seller') || userRole === 'creator';
  const isOrderSeller = currentUser?.uid === sellerId;
  const isOrderCustomer = currentUser?.uid === userId;
  
  // If user has customer role, they should never be considered a seller
  const isSeller = (hasSellerRole || isOrderSeller) && userRole !== 'customer';
  const isCustomer = isOrderCustomer || userRole === 'customer';
  
  // Show action buttons for both sellers and customers
  // Sellers: move to next status, cancel, message customer
  // Customers: message seller
  // Always show seller footer if they're a seller (they can always message customer)
  const showSellerFooter = isSeller && !isCustomer;
  const showCustomerFooter = isCustomer && !isSeller;
  const showFooter = showSellerFooter || showCustomerFooter;
  
  // Debug workflow and footer logic
  console.log('[OrderDetails] Order status (original):', order.status);
  console.log('[OrderDetails] Order status (resolved):', orderStatus);
  console.log('[OrderDetails] Workflow key:', workflowKey);
  console.log('[OrderDetails] Workflow:', workflow);
  console.log('[OrderDetails] workflow.action:', workflow.action);
  console.log('[OrderDetails] canCancel:', canCancel);
  console.log('[OrderDetails] showSellerFooter:', showSellerFooter);
  console.log('[OrderDetails] showCustomerFooter:', showCustomerFooter);
  console.log('[OrderDetails] showFooter:', showFooter);
  
  // Debug user role detection
  console.log('[OrderDetails] User role debug:');
  console.log('[OrderDetails] currentUser?.uid:', currentUser?.uid);
  console.log('[OrderDetails] userRole:', userRole);
  console.log('[OrderDetails] hasRole("seller"):', hasRole('seller'));
  console.log('[OrderDetails] hasSellerRole:', hasSellerRole);
  console.log('[OrderDetails] userRole === "creator":', userRole === 'creator');
  console.log('[OrderDetails] order.sellerId:', order.sellerId);
  console.log('[OrderDetails] order.userId:', order.userId);
  console.log('[OrderDetails] sellerId (resolved):', sellerId);
  console.log('[OrderDetails] userId (resolved):', userId);
  console.log('[OrderDetails] isOrderSeller:', isOrderSeller);
  console.log('[OrderDetails] isOrderCustomer:', isOrderCustomer);
  console.log('[OrderDetails] isSeller:', isSeller);
  console.log('[OrderDetails] isCustomer:', isCustomer);
  console.log('[OrderDetails] showSellerFooter:', showSellerFooter);
  console.log('[OrderDetails] showCustomerFooter:', showCustomerFooter);
  console.log('[OrderDetails] showFooter:', showFooter);
  // Debug logs
  console.log('[OrderDetails] order:', order);
  console.log('[OrderDetails] order.userId:', order?.userId);
  console.log('[OrderDetails] order.sellerId:', order?.sellerId);
  console.log('[OrderDetails] order.merchantOrders:', order?.merchantOrders);
  if (order?.merchantOrders?.[0]?.items) {
    console.log('[OrderDetails] merchant order items:', order.merchantOrders[0].items);
    console.log('[OrderDetails] item statuses:', order.merchantOrders[0].items.map(item => item.status));
  }
  console.log('[OrderDetails] workflowKey:', workflowKey);
  console.log('[OrderDetails] workflow:', workflow);
  console.log('[OrderDetails] canCancel:', canCancel, 'showFooter:', showFooter);
  console.log('[OrderDetails] order.items:', order.items);
  if (Array.isArray(order.items)) {
    order.items.forEach((item, idx) => {
      console.log(`[OrderDetails] item[${idx}]:`, item, 'type:', typeof item);
    });
  }

  async function handleStatusUpdate(newStatus) {
    setUpdating(true);
    try {
      await updateOrderStatus(order.id, newStatus);
      // Refetch order to update UI
      const updated = await getOrderById(order.id);
      setOrder(updated);
    } catch (err) {
      orderToasts.error('update');
    } finally {
      setUpdating(false);
    }
  }

  let itemsContent;
  try {
    itemsContent = (
      Array.isArray(order.items) && order.items.some(item => typeof item === 'object' && item !== null) ? (
        order.items
          .map((item, originalIdx) => ({ item, originalIdx }))
          .filter(({ item }) => typeof item === 'object' && item !== null)
          .map(({ item, originalIdx }) => (
            <div key={item.id || originalIdx} style={{ display: 'flex', alignItems: 'center', gap: 12, background: NEUTRAL_WHITE, borderRadius: 10, padding: '10px 14px', boxShadow: '0 1px 4px #0001' }}>
              <img src={item.image || PLACEHOLDER_IMAGE} alt={item.name || 'Item'} style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover' }} />
              <div style={{ flex: 1 }}>
                                  <div style={{ fontSize: 12, color: NEUTRAL_TEXT, ...bodyFont }}>{item.name || 'Unnamed Item'}</div>
                  {item.variant && Object.keys(item.variant).length > 0 && (
                    <div style={{ fontSize: 12, color: ACCENT_GRAY, ...bodyFont }}>
                      {Object.entries(item.variant).map(([key, value]) => `${key}: ${value}`).join(', ')}
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: ACCENT_GRAY, ...bodyFont }}>${Number(item.unitPrice ?? 0).toFixed(2)}</div>
                  {(item.qty || item.quantity || 1) > 1 && <div style={{ fontSize: 12, color: ACCENT_GRAY, ...bodyFont }}>x{(item.qty || item.quantity) ?? 1}</div>}
                  {item.fulfillmentDay && (
                    <div style={{ fontSize: 12, color: '#3B82F6', ...bodyFont }}>
                      Fulfillment: {new Date(item.fulfillmentDay + 'T00:00:00').toLocaleDateString()}
                    </div>
                  )}
              </div>
              {isSeller && (
              <button 
                onClick={() => setShowCancelItem(originalIdx)} 
                  style={{ background: '#fff', color: '#BDBDBD', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 12, fontWeight: 600, cursor: 'pointer', boxShadow: '0 2px 8px #0001', ...bodyFont }}
                aria-label="Cancel Item"
              >
                Cancel Item
              </button>
              )}
            </div>
          ))
      ) : (
        <div style={{ color: ACCENT_GRAY, fontSize: 12 }}>No items found.</div>
      )
    );
  } catch (err) {
    itemsContent = <div style={{ color: 'red' }}>Error rendering items: {err.message}</div>;
    console.error('[OrderDetails] Error rendering items:', err, order.items);
  }

  const STATUS_TAG_STYLES = {
    new: { bg: '#F3E8FF', text: '#6D28D9' },
    accepted: { bg: '#E3F2FD', text: '#1565C0' },
    preparing: { bg: '#FFFDE7', text: '#F9A825' },
    ready: { bg: '#FFFDE7', text: '#F9A825' },
    delivering: { bg: '#FFF3E0', text: '#EF6C00' },
    completed: { bg: '#E8F5E9', text: '#2E7D32' },
    canceled: { bg: '#FFEBEE', text: '#C62828' },
  };

  return (
    <div style={{ minHeight: '100vh', background: NEUTRAL_BG }}>
      {/* Header */}
      <header style={{ position: 'sticky', top: 0, zIndex: 10, background: NEUTRAL_WHITE, borderBottom: '1px solid #eee', padding: '12px 0 0 0', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '0 20px 12px 20px' }}>
          <BackButton
            onClick={() => navigate(-1)}
            variant="standard"
            ariaLabel="Back"
          />
          <h1 style={{ ...headingFont, fontSize: 12, color: NEUTRAL_TEXT, margin: 0 }}>Order Details</h1>
        </div>
      </header>

      {/* Order Summary Block */}
      <section style={{ background: NEUTRAL_WHITE, borderRadius: 18, boxShadow: '0 2px 8px #0001', margin: '0 auto 20px auto', maxWidth: 540, padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 12, color: ACCENT_GRAY, ...bodyFont }}>{formatOrderNumber(order.id || orderId)}</div>
          <span
            style={{
              background: '#F5F5F5',
              color: '#666666',
              borderRadius: 8,
              padding: '4px 14px',
              fontWeight: 700,
              fontSize: 12,
              letterSpacing: 1,
              ...bodyFont
            }}
            aria-label={workflow.tag}
          >
            {workflow.tag}
          </span>
        </div>
        <div style={{ fontSize: 12, color: ACCENT_GRAY, ...bodyFont }}>Order Date: {(() => {
          // Try to get a valid date from various possible fields
          const dateFields = [
            order.date, 
            order.createdAt, 
            order.timestamp, 
            order.orderDate, 
            order.order_date,
            order.orderTime,
            order.order_time,
            order.placedAt,
            order.placed_at,
            order.orderPlacedAt,
            order.order_placed_at,
            order.time,
            order.timeCreated,
            order.time_created
          ];
          let validDate = null;
          
          for (let i = 0; i < dateFields.length; i++) {
            const field = dateFields[i];
            const fieldName = ['date', 'createdAt', 'timestamp', 'orderDate', 'order_date', 'orderTime', 'order_time', 'placedAt', 'placed_at', 'orderPlacedAt', 'order_placed_at', 'time', 'timeCreated', 'time_created'][i];
            
            if (field) {
              console.log(`[OrderDetails] Checking field '${fieldName}':`, field);
              try {
                // Handle Firestore Timestamp objects
                if (field && typeof field === 'object' && (field._seconds || field.seconds)) {
                  const seconds = field._seconds || field.seconds;
                  validDate = new Date(seconds * 1000);
                  console.log(`[OrderDetails] Found valid Firestore Timestamp in '${fieldName}':`, validDate);
                  break;
                }
                // Handle regular date strings or Date objects
                const parsed = new Date(field);
                if (!isNaN(parsed.getTime())) {
                  validDate = parsed;
                  console.log(`[OrderDetails] Found valid date in '${fieldName}':`, validDate);
                  break;
                }
              } catch (e) {
                console.log(`[OrderDetails] Failed to parse date field '${fieldName}':`, field, e);
              }
            }
          }
          
          // If no valid date found, use current date
          if (!validDate) {
            validDate = new Date();
            console.log('[OrderDetails] No valid date found, using current date');
          }
          
          return `${formatOrderDate(validDate)} ${formatOrderTime(validDate)}`;
        })()}</div>
      </section>

      {/* Status Tracker */}
      <section aria-label="Order Status Timeline" style={{ maxWidth: 540, margin: '0 auto 24px auto', padding: '0 8px' }}>
        <ol style={{
          listStyle: 'none',
          margin: 0,
          padding: 0,
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          height: 260, // keep even spacing
          justifyContent: 'space-between',
        }}>
          {(() => {
            // Define workflow steps for pickup and delivery
            const isDelivery = order.type === 'delivery';
            const steps = isDelivery
              ? [
                  { key: 'confirmed', label: 'Order Confirmed' },
                  { key: 'in-progress', label: 'In Progress' },
                  { key: 'out-for-delivery', label: 'Out for Delivery' },
                  { key: 'completed', label: 'Completed' },
                ]
              : [
                  { key: 'confirmed', label: 'Order Confirmed' },
                  { key: 'in-progress', label: 'In Progress' },
                  { key: 'ready', label: 'Ready for Pick-up' },
                  { key: 'completed', label: 'Completed' },
                ];
            // Find the current step index
            const statusToStepIdx = (status) => {
              switch (status) {
                case 'placed':
                case 'confirmed': return 0;
                case 'accepted': return 1; // accepted should be "In Progress" step
                case 'processing':
                case 'in-progress': return 1;
                case 'ready': return isDelivery ? 1 : 2;
                case 'out-for-delivery': return 2;
                case 'delivered':
                case 'completed': return 3;
                case 'cancelled':
                case 'canceled': return 3;
                default: return 0;
              }
            };
            // For multi-vendor orders, determine status from item statuses (same as product group)
            let orderStatus = order.status;
            if (order.merchantOrders && order.merchantOrders.length > 0) {
              // Get all items from all merchant orders
              const allItems = [];
              order.merchantOrders.forEach(mo => {
                if (mo.items && Array.isArray(mo.items)) {
                  allItems.push(...mo.items);
                }
              });
              
              if (allItems.length > 0) {
                // Use the same logic as getGroupStatus for consistency
                const itemStatuses = allItems.map(item => item.status).filter(Boolean);
                console.log('[OrderDetails] Item statuses:', itemStatuses);
                
                const statusPriority = {
                  'completed': 5, 'COMPLETED': 5,
                  'ready': 4, 'READY': 4,
                  'in-progress': 3, 'IN-PROGRESS': 3,
                  'accepted': 2, 'ACCEPTED': 2,
                  'placed': 1, 'PLACED': 1
                };
                
                const highestPriority = Math.max(...itemStatuses.map(status => statusPriority[status] || 0));
                orderStatus = itemStatuses.find(status => statusPriority[status] === highestPriority) || orderStatus;
                console.log('[OrderDetails] Calculated order status from items:', orderStatus, 'from item statuses:', itemStatuses);
              } else {
                // Fallback to merchant order status if no items
                const merchantStatuses = order.merchantOrders.map(mo => mo.status).filter(Boolean);
                console.log('[OrderDetails] No items found, using merchant statuses:', merchantStatuses);
                if (merchantStatuses.length > 0) {
                  const statusPriority = {
                    'completed': 5, 'COMPLETED': 5,
                    'ready': 4, 'READY': 4,
                    'in-progress': 3, 'IN-PROGRESS': 3,
                    'accepted': 2, 'ACCEPTED': 2,
                    'placed': 1, 'PLACED': 1
                  };
                  const highestPriority = Math.max(...merchantStatuses.map(status => statusPriority[status] || 0));
                  orderStatus = merchantStatuses.find(status => statusPriority[status] === highestPriority) || orderStatus;
                }
              }
            }
            const currentStepIdx = statusToStepIdx(orderStatus);
            // Optionally, get timestamps for each step if available
            const statusTimestamps = {
              confirmed: order.date || order.createdAt,
              'in-progress': order.processingAt,
              ready: order.readyAt,
              'out-for-delivery': order.outForDeliveryAt,
              completed: order.completedAt || order.deliveredAt,
            };
            return steps.map((step, idx) => {
              const isCompleted = idx < currentStepIdx;
              const isCurrent = idx === currentStepIdx;
              const isFuture = idx > currentStepIdx;
              let iconBg = isCompleted ? PRIMARY_BROWN : isCurrent ? SUCCESS_GREEN : '#fff';
              let iconBorder = isCompleted || isCurrent ? PRIMARY_BROWN : '#ccc';
              let iconColor = isCompleted || isCurrent ? '#fff' : '#bbb';
              let textColor = isCompleted || isCurrent ? PRIMARY_BROWN : ACCENT_GRAY;
              let fontWeight = isCurrent ? 700 : 500;
              let opacity = isFuture ? 0.5 : 1;
              return (
                <li key={step.key} style={{
                  display: 'flex',
                  alignItems: 'flex-start', // <-- align to top
                  gap: 16,
                  flex: 1,
                  minHeight: 0,
                  opacity,
                  position: 'relative',
                }}>
                  {/* Progress bar and circle */}
                  <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', paddingTop: 2 }}>
                    <div style={{
                      width: 18,
                      height: 18,
                      borderRadius: 9,
                      background: isCompleted ? PRIMARY_BROWN : '#fff',
                      border: `2px solid ${isCompleted ? PRIMARY_BROWN : '#ccc'}`,
                      color: iconColor,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: 12,
                      zIndex: 1,
                    }}>
                      {isCompleted ? '✓' : ''}
                    </div>
                    {idx < steps.length - 1 && (
                      <div style={{
                        position: 'absolute',
                        top: 18,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: 2,
                        height: 'calc(100% - 18px)',
                        background: isCompleted ? PRIMARY_BROWN : '#eee',
                        minHeight: 32,
                        zIndex: 0,
                      }} />
                    )}
                  </div>
                  {/* Step text */}
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                      <span style={{ fontWeight, color: textColor, fontSize: 12, ...bodyFont }}>{step.label}</span>
                    </div>
                    <div style={{ fontSize: 12, color: ACCENT_GRAY, ...bodyFont }}>
                      {(() => {
                        const timestamp = statusTimestamps[step.key];
                        if (timestamp) {
                          // Try to parse the timestamp
                          let validDate = null;
                          try {
                            // Handle Firestore Timestamp objects
                            if (timestamp && typeof timestamp === 'object' && (timestamp._seconds || timestamp.seconds)) {
                              const seconds = timestamp._seconds || timestamp.seconds;
                              validDate = new Date(seconds * 1000);
                            } else {
                              // Handle regular date strings or Date objects
                              const parsed = new Date(timestamp);
                              if (!isNaN(parsed.getTime())) {
                                validDate = parsed;
                              }
                            }
                          } catch (e) {
                            console.log('[OrderDetails] Failed to parse status timestamp:', timestamp, e);
                          }
                          
                          if (validDate) {
                            return `${formatOrderDate(validDate)} ${formatOrderTime(validDate)}`;
                          }
                        }
                        
                        return isCompleted ? 'Completed' : '';
                      })()}
                    </div>
                  </div>
                </li>
              );
            });
          })()}
        </ol>
      </section>

      {/* Order Items List - Grouped by Seller and Service Date */}
      <section aria-label="Order Items" style={{ maxWidth: 540, margin: '0 auto 24px auto', padding: '0 8px' }}>
        <h2 style={{ ...headingFont, fontSize: 12, color: NEUTRAL_TEXT, margin: '0 0 12px 0' }}>Items</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {(() => {
            try {
              // Group items by seller and service date
              const groups = {};
              
              if (order.merchantOrders) {
                // New multi-vendor structure
                order.merchantOrders.forEach(merchantOrder => {
                  merchantOrder.items?.forEach(item => {
                    const key = `${merchantOrder.sellerId}-${item.serviceDate}`;
                    if (!groups[key]) {
                      groups[key] = {
                        sellerId: merchantOrder.sellerId,
                        sellerName: shopNames[merchantOrder.sellerId] || merchantOrder.sellerName || `Seller ${merchantOrder.sellerId.slice(-4)}`,
                        serviceDate: item.serviceDate,
                        windowStart: item.windowStartLocal,
                        windowEnd: item.windowEndLocal,
                        status: item.status,
                        items: []
                      };
                    }
                    groups[key].items.push(item);
                  });
                });
              } else if (order.items) {
                // Fallback to old structure
                order.items.forEach(item => {
                  const key = `${item.sellerId || 'default'}-${item.fulfillmentDay || 'default'}`;
                  if (!groups[key]) {
                    groups[key] = {
                      sellerId: item.sellerId || 'default',
                      sellerName: item.sellerName || 'Seller',
                      serviceDate: item.fulfillmentDay || 'default',
                      items: []
                    };
                  }
                  groups[key].items.push(item);
                });
              }

              return Object.values(groups).map((group, groupIndex) => {
                const getDateLabel = (serviceDate) => {
                  const today = new Date();
                  const tomorrow = new Date(today);
                  tomorrow.setDate(tomorrow.getDate() + 1);
                  
                  const orderDate = new Date(serviceDate + 'T00:00:00');
                  
                  if (orderDate.toDateString() === today.toDateString()) {
                    return 'Today';
                  } else if (orderDate.toDateString() === tomorrow.toDateString()) {
                    return 'Tomorrow';
                  } else {
                    return orderDate.toLocaleDateString('en-US', { 
                      weekday: 'short', 
                      month: 'short', 
                      day: 'numeric' 
                    });
                  }
                };

                const getStatusColor = (status) => {
                  switch (status) {
                    case 'placed': return { bg: '#F3E8FF', text: '#6D28D9' };
                    case 'accepted': return { bg: '#E3F2FD', text: '#1565C0' };
                    case 'ready': return { bg: '#FFFDE7', text: '#F9A825' };
                    case 'completed': return { bg: '#E8F5E9', text: '#2E7D32' };
                    case 'cancelled': return { bg: '#FFEBEE', text: '#C62828' };
                    default: return { bg: '#F5F5F5', text: '#666666' };
                  }
                };

                const statusColors = getStatusColor(group.status);
                
                // Check if status is "ready" and we have an address
                const isReadyStatus = group.status?.toLowerCase() === 'ready';
                const shopAddress = shopAddresses[group.sellerId];

                return (
                  <div key={groupIndex} style={{ background: NEUTRAL_WHITE, borderRadius: 10, padding: 16, boxShadow: '0 1px 4px #0001' }}>
                    {/* Group Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid #F0F0F0' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ fontSize: 12, color: NEUTRAL_TEXT, fontWeight: 600, ...bodyFont }}>
                          {group.sellerName}
                        </div>
                        <span style={{ 
                          background: '#E3F2FD', 
                          color: '#1565C0', 
                          borderRadius: 6, 
                          padding: '2px 8px', 
                          fontSize: 10, 
                          fontWeight: 600,
                          ...bodyFont 
                        }}>
                          {getDateLabel(group.serviceDate)}
                        </span>
                        {group.windowStart && group.windowEnd && (
                          <span style={{ fontSize: 10, color: ACCENT_GRAY, ...bodyFont }}>
                            {group.windowStart} - {group.windowEnd}
                          </span>
                        )}
                      </div>
                      <span style={{
                        background: statusColors.bg,
                        color: statusColors.text,
                        borderRadius: 6,
                        padding: '2px 8px',
                        fontSize: 10,
                        fontWeight: 600,
                        ...bodyFont
                      }}>
                        {group.status?.toUpperCase() || 'PLACED'}
                      </span>
                    </div>

                    {/* Shop Address - Only show when status is "ready" and address exists */}
                    {isReadyStatus && shopAddress && (shopAddress.street || shopAddress.city) && (
                      <div style={{ 
                        background: '#FFFDE7', 
                        borderRadius: 8, 
                        padding: 12, 
                        marginBottom: 12,
                        border: '1px solid #F9A82533'
                      }}>
                        <div style={{ fontSize: 11, color: '#F9A825', fontWeight: 700, marginBottom: 6, ...bodyFont }}>
                          📍 Pickup Address
                        </div>
                        <div style={{ fontSize: 12, color: NEUTRAL_TEXT, lineHeight: 1.6, ...bodyFont }}>
                          {shopAddress.street && <div>{shopAddress.street}</div>}
                          {shopAddress.city && (
                            <div>
                              {shopAddress.city}
                              {shopAddress.state && `, ${shopAddress.state}`}
                              {shopAddress.zip && ` ${shopAddress.zip}`}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Group Items */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {group.items.map((item, itemIndex) => (
                        <div key={item.id || itemIndex} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <img 
                            src={item.productSnapshot?.image || item.image || PLACEHOLDER_IMAGE} 
                            alt={item.productSnapshot?.name || item.name || 'Item'} 
                            style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover' }} 
                          />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 12, color: NEUTRAL_TEXT, ...bodyFont }}>
                              {item.productSnapshot?.name || item.name || 'Unnamed Item'}
                            </div>
                            <div style={{ fontSize: 12, color: ACCENT_GRAY, ...bodyFont }}>
                              ${Number(item.unitPrice || item.price || 0).toFixed(2)}
                            </div>
                            {(item.quantity || item.qty || 1) > 1 && (
                              <div style={{ fontSize: 12, color: ACCENT_GRAY, ...bodyFont }}>
                                x{item.quantity || item.qty || 1}
                              </div>
                            )}
                          </div>
                          {/* Write Review Button for Customers on Completed Items - Only show if no review exists */}
                          {(() => {
                            const itemStatus = item.status || item.itemStatus;
                            const actualProductId = item.productId || item.productSnapshot?.id || item.id;
                            const shouldShowReviewButton = isCustomer && itemStatus === 'completed' && !existingReviews.find(r => r.productId === actualProductId);
                            console.log('🔍 Review button condition check:', {
                              itemId: item.id,
                              actualProductId,
                              itemName: item.name || item.productName || item.title || item.productSnapshot?.name,
                              itemStatus,
                              isCustomer,
                              orderStatus,
                              existingReviewsCount: existingReviews.length,
                              hasExistingReview: !!existingReviews.find(r => r.productId === actualProductId),
                              shouldShowReviewButton
                            });
                            return shouldShowReviewButton;
                          })() && (
                            <button
                              onClick={() => {
                                const actualProductId = item.productId || item.productSnapshot?.id || item.id;
                                // Check if review already exists for this product
                                const existingReview = existingReviews.find(r => r.productId === actualProductId);
                                
                                // Set the specific product for review
                                setReviewForms(prev => ({
                                  ...prev,
                                  [item.id]: {
                                    rating: existingReview?.rating || 0,
                                    comment: existingReview?.comment || '',
                                    existingReviewId: existingReview?.id || null
                                  }
                                }));
                                setShowReviews(true);
                              }}
                              style={{
                                background: 'white',
                                border: '1px solid #C97B53',
                                borderRadius: 8,
                                padding: '6px 12px',
                                fontSize: 11,
                                fontWeight: 600,
                                color: '#C97B53',
                                cursor: 'pointer',
                                ...bodyFont,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseOver={(e) => {
                                e.target.style.background = '#C97B53';
                                e.target.style.color = 'white';
                              }}
                              onMouseOut={(e) => {
                                e.target.style.background = 'white';
                                e.target.style.color = '#C97B53';
                              }}
                            >
                              Write Review
                            </button>
                          )}
                          {isSeller && (
                            <button 
                              onClick={() => setShowCancelItem(item.id)} 
                              style={{ 
                                background: '#fff', 
                                color: '#BDBDBD', 
                                border: 'none', 
                                borderRadius: 8, 
                                padding: '8px 16px', 
                                fontSize: 12, 
                                fontWeight: 600, 
                                cursor: 'pointer', 
                                boxShadow: '0 2px 8px #0001',
                                ...bodyFont 
                              }}
                              aria-label="Cancel Item"
                            >
                              Cancel Item
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              });
            } catch (err) {
              console.error('[OrderDetails] Error rendering grouped items:', err);
              return (
                <div style={{ color: ACCENT_GRAY, fontSize: 12 }}>
                  Error rendering items. Please try refreshing the page.
                </div>
              );
            }
          })()}
        </div>
      </section>


      {/* Review Form Modal - Only show for customers on completed items */}
      {showReviews && isCustomer && (
        <section aria-label="Product Review" style={{ maxWidth: 540, margin: '0 auto 24px auto', padding: '0 8px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h2 style={{ ...headingFont, fontSize: 12, color: NEUTRAL_TEXT, margin: 0 }}>Write Review</h2>
            <button
              onClick={() => setShowReviews(false)}
              style={{
                background: '#F5F5F5',
                color: NEUTRAL_TEXT,
                border: 'none',
                borderRadius: 8,
                padding: '8px 16px',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                ...bodyFont
              }}
            >
              Close
            </button>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {(() => {
              // Get the product being reviewed
              const productBeingReviewed = (() => {
                if (order.merchantOrders) {
                  for (const merchantOrder of order.merchantOrders) {
                    for (const item of merchantOrder.items || []) {
                      if (reviewForms[item.id]) {
                        console.log('🔍 Product being reviewed - full item object:', item);
                        console.log('🔍 Product name fields:', {
                          name: item.name,
                          productName: item.productName,
                          title: item.title,
                          productSnapshot: item.productSnapshot
                        });
                        return {
                          id: item.productId || item.productSnapshot?.id || item.id, // Use actual product ID, fallback to item ID
                          name: item.name || item.productName || item.title || item.productSnapshot?.name || 'Unnamed Product',
                          image: item.image || item.productSnapshot?.image,
                          sellerName: shopNames[merchantOrder.sellerId] || merchantOrder.sellerName || 'Seller'
                        };
                      }
                    }
                  }
                } else if (order.items) {
                  for (const item of order.items) {
                    if (reviewForms[item.id]) {
                      console.log('🔍 Product being reviewed - full item object:', item);
                      return {
                        id: item.productId || item.productSnapshot?.id || item.id, // Use actual product ID, fallback to item ID
                        name: item.name || item.productName || item.title || item.productSnapshot?.name || 'Unnamed Product',
                        image: item.image || item.productSnapshot?.image,
                        sellerName: item.sellerName || 'Seller'
                      };
                    }
                  }
                }
                return null;
              })();
              
              if (!productBeingReviewed) {
                return (
                  <div style={{
                    background: NEUTRAL_WHITE,
                    borderRadius: 12,
                    padding: 20,
                    textAlign: 'center',
                    color: ACCENT_GRAY,
                    fontSize: 14
                  }}>
                    <p style={{ margin: 0 }}>No product selected for review.</p>
                  </div>
                );
              }
              
              return (
                <div style={{
                  background: NEUTRAL_WHITE,
                  borderRadius: 12,
                  padding: 16,
                  border: '1px solid #E5E7EB'
                }}>
                  <ReviewForm
                    product={productBeingReviewed}
                    onSubmit={(reviewData) => handleReviewSubmit(productBeingReviewed.id, reviewData)}
                    onCancel={() => setShowReviews(false)}
                    existingReview={existingReviews.find(r => r.productId === productBeingReviewed.id)}
                    isSubmitting={isSubmittingReviews}
                  />
                </div>
              );
            })()}
          </div>
        </section>
      )}

      {/* Order Summary Breakdown */}
      <section aria-label="Order Summary" style={{ maxWidth: 540, margin: '0 auto 24px auto', padding: '0 8px' }}>
        <h2 style={{ ...headingFont, fontSize: 12, color: NEUTRAL_TEXT, margin: '0 0 12px 0' }}>Summary</h2>
        <div style={{ background: NEUTRAL_WHITE, borderRadius: 10, padding: 16, boxShadow: '0 1px 4px #0001', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: ACCENT_GRAY, ...bodyFont }}>
            <span>Subtotal</span>
            <span>${subtotal.toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: ACCENT_GRAY, ...bodyFont }}>
            <span>Service Fee</span>
            <span>${shipping.toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: ACCENT_GRAY, ...bodyFont }}>
            <span>Tax</span>
            <span>${tax.toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: NEUTRAL_TEXT, fontWeight: 700, ...bodyFont, marginTop: 8 }}>
            <span>Total</span>
            <span>${total.toFixed(2)}</span>
          </div>
        </div>
      </section>

      {/* Shipping Address Block */}
      {(order.parentOrder?.shippingAddress || order.shippingAddress) && (
        <section aria-label="Shipping Address" style={{ maxWidth: 540, margin: '0 auto 24px auto', padding: '0 8px' }}>
          <h2 style={{ ...headingFont, fontSize: 12, color: NEUTRAL_TEXT, margin: '0 0 12px 0' }}>Shipping Address</h2>
          <div style={{ background: NEUTRAL_WHITE, borderRadius: 10, padding: 16, boxShadow: '0 1px 4px #0001', fontSize: 12, color: NEUTRAL_TEXT, ...bodyFont }}>
            <div>{order.parentOrder?.shippingAddress?.name || order.shippingAddress?.name || 'No Name Provided'}</div>
            <div>{order.parentOrder?.shippingAddress?.street || order.shippingAddress?.street || 'No Street Provided'}</div>
            <div>{order.parentOrder?.shippingAddress?.city || order.shippingAddress?.city || 'No City Provided'}, {order.parentOrder?.shippingAddress?.zip || order.shippingAddress?.zip || 'No Zip Provided'}</div>
            <div>{order.parentOrder?.shippingAddress?.phone || order.shippingAddress?.phone || 'No Phone Provided'}</div>
          </div>
        </section>
      )}

      {/* Sticky Action Footer for Order Workflow */}
      {showFooter && (
        <footer style={{ position: 'sticky', bottom: 0, zIndex: 20, background: 'rgba(255,255,255,0.97)', padding: '18px 0 18px 0', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'center', gap: 16, boxShadow: '0 -2px 8px #0001' }}>
          
          {/* Seller Actions */}
          {showSellerFooter && (
            <>
              {/* Cancel Order Button - Only show if order can be cancelled */}
              {canCancel && (
                <button
                  onClick={() => setShowCancelOrder(true)}
                  style={{
                    background: '#fff',
                    color: ACCENT_GRAY,
                    border: 'none',
                    borderRadius: 12,
                    padding: '14px 40px',
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: 'pointer',
                    ...bodyFont,
                    boxShadow: '0 2px 8px #0001',
                    opacity: 1
                  }}
                  aria-label="Cancel Order"
                >
                  Cancel Order
                </button>
              )}
              
              {/* Move to Next Status Button - Only show if there's a workflow action */}
              {workflow.action && (
                <button
                  onClick={() => handleStatusUpdate(workflow.next)}
                  disabled={updating}
                  style={{
                    ...GRADIENT_UTILS.getGradientButton(),
                    borderRadius: 12,
                    padding: '14px 40px',
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: updating ? 'not-allowed' : 'pointer',
                    ...bodyFont,
                    boxShadow: '0 2px 8px #0001',
                    opacity: updating ? 0.7 : 1,
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    if (!updating) {
                      e.target.style.transform = 'scale(1.02)';
                      e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                    }
                  }}
                  onMouseOut={(e) => {
                    e.target.style.transform = 'scale(1)';
                    e.target.style.boxShadow = '0 2px 8px #0001';
                  }}
                  aria-label={workflow.action}
                >
                  {workflow.action}
                </button>
              )}
              
              {/* Message Customer Button */}
              <button
                onClick={() => setIsChatOpen(true)}
                style={{
                  background: '#fff',
                  color: '#757575',
                  border: 'none',
                  borderRadius: 12,
                  padding: '14px 24px',
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: 'pointer',
                  ...bodyFont,
                  boxShadow: '0 2px 8px #0001',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                aria-label="Message customer about this order"
              >
                Message
              </button>
            </>
          )}
          
          {/* Customer Actions */}
          {showCustomerFooter && (
            <>
              {/* Message Seller Button */}
              <button
                onClick={() => setIsChatOpen(true)}
                style={{
                  ...GRADIENT_UTILS.getGradientButton(),
                  borderRadius: 12,
                  padding: '14px 40px',
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: 'pointer',
                  ...bodyFont,
                  boxShadow: '0 2px 8px #0001',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.transform = 'scale(1.02)';
                  e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                }}
                onMouseOut={(e) => {
                  e.target.style.transform = 'scale(1)';
                  e.target.style.boxShadow = '0 2px 8px #0001';
                }}
                aria-label="Message seller about this order"
              >
                Message
              </button>
            </>
          )}
        </footer>
      )}

      <ConfirmationModal
        open={showCancelOrder}
        onClose={() => setShowCancelOrder(false)}
        onConfirm={handleCancelOrder}
        title="Confirm Order Cancellation"
        message={`Are you sure you want to cancel order #${order.id}? This action cannot be undone.`}
        reason={cancelReason}
        setReason={setCancelReason}
        confirmLabel="Confirm Cancellation"
      />

      {showCancelItem !== null && (
        <ConfirmationModal
          open={true}
          onClose={() => setShowCancelItem(null)}
          onConfirm={() => handleCancelItem(showCancelItem)}
          title={`Confirm Item Cancellation`}
          message={`Are you sure you want to cancel item "${getItemName(order.items, showCancelItem)}"? This action cannot be undone.`}
          reason={cancelReason}
          setReason={setCancelReason}
          confirmLabel="Confirm Cancellation"
        />
      )}
      
      {/* Order Chat Modal */}
      {order && isChatOpen && (
        isCustomer ? (
          <CustomerOrderChat
            orderId={order.id}
            isOpen={isChatOpen}
            onClose={() => setIsChatOpen(false)}
            order={order}
          />
        ) : (
        <OrderChat
          orderId={order.id}
          isOpen={isChatOpen}
          onClose={() => setIsChatOpen(false)}
          order={order}
        />
        )
      )}
    </div>
  );
} 